import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ChevronLeft, Edit, User, Mail, Phone, MapPin, Calendar, Clock, 
  CheckCircle2, FileText, MessageSquare, TrendingUp, Target, Plus,
  ArrowRight, RefreshCw, Trash2, Phone as PhoneIcon, Send, Users,
  CalendarClock, Euro, AlertCircle, Check
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { companyApi } from '../../../lib/api';

// Commercial status configuration
const COMMERCIAL_STATUS = {
  pending: { label: 'En cours', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  transformed: { label: 'Transformé', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  no_response: { label: 'Sans réponse', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
  to_followup: { label: 'À relancer', color: 'bg-orange-100 text-orange-800', icon: RefreshCw },
  lost: { label: 'Perdu', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  postponed: { label: 'Reporté', color: 'bg-purple-100 text-purple-800', icon: CalendarClock },
};

const FOLLOWUP_TYPES = {
  call: { label: 'Appel', icon: PhoneIcon },
  email: { label: 'Email', icon: Send },
  sms: { label: 'SMS', icon: MessageSquare },
  meeting: { label: 'RDV', icon: Users },
  other: { label: 'Autre', icon: FileText },
};

export function ClientDetailPageNew() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [client, setClient] = useState(null);
  const [stats, setStats] = useState(null);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isFollowupDialogOpen, setIsFollowupDialogOpen] = useState(false);
  const [isExchangeDialogOpen, setIsExchangeDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form states
  const [statusForm, setStatusForm] = useState({ commercial_status: '', commercial_note: '' });
  const [followupForm, setFollowupForm] = useState({ 
    followup_type: 'call', content: '', next_followup_date: '', outcome: '' 
  });
  const [exchangeForm, setExchangeForm] = useState({
    exchange_type: 'call', direction: 'outgoing', subject: '', content: '', duration_minutes: ''
  });

  // Fetch all data
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [clientRes, collabRes, exchangesRes, statsRes, quoteRes] = await Promise.all([
        companyApi.getClient(clientId),
        companyApi.getCollaborators().catch(() => ({ data: [] })),
        companyApi.getClientExchanges(clientId).catch(() => ({ data: [] })),
        companyApi.getClientStats(clientId).catch(() => ({ data: null })),
        companyApi.getClientQuoteRequests(clientId).catch(() => ({ data: [] })),
      ]);
      
      setClient(clientRes.data);
      setCollaborators(collabRes.data);
      setExchanges(exchangesRes.data || []);
      setStats(statsRes.data || statsRes);
      setQuoteRequests(quoteRes.data || quoteRes || []);
    } catch (error) {
      console.error('Error fetching client data:', error);
      toast.error('Client non trouvé');
      navigate('/company/clients');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientId]);

  // Handlers
  const handleUpdateStatus = async () => {
    if (!selectedRequest || !statusForm.commercial_status) return;
    
    try {
      await companyApi.updateCommercialStatus(selectedRequest.id, statusForm);
      toast.success('Statut mis à jour');
      setIsStatusDialogOpen(false);
      setStatusForm({ commercial_status: '', commercial_note: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAddFollowup = async () => {
    if (!selectedRequest || !followupForm.content) return;
    
    try {
      await companyApi.createFollowup(selectedRequest.id, followupForm);
      toast.success('Relance enregistrée');
      setIsFollowupDialogOpen(false);
      setFollowupForm({ followup_type: 'call', content: '', next_followup_date: '', outcome: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleAddExchange = async () => {
    if (!exchangeForm.content && !exchangeForm.subject) {
      toast.error('Veuillez ajouter un sujet ou un contenu');
      return;
    }
    
    try {
      await companyApi.createClientExchange(clientId, {
        ...exchangeForm,
        duration_minutes: exchangeForm.duration_minutes ? parseInt(exchangeForm.duration_minutes) : null
      });
      toast.success('Échange enregistré');
      setIsExchangeDialogOpen(false);
      setExchangeForm({ exchange_type: 'call', direction: 'outgoing', subject: '', content: '', duration_minutes: '' });
      const res = await companyApi.getClientExchanges(clientId);
      setExchanges(res.data || []);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteExchange = async (exchangeId) => {
    if (!window.confirm('Supprimer cet échange ?')) return;
    try {
      await companyApi.deleteClientExchange(clientId, exchangeId);
      toast.success('Échange supprimé');
      const res = await companyApi.getClientExchanges(clientId);
      setExchanges(res.data || []);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openStatusDialog = (request) => {
    setSelectedRequest(request);
    setStatusForm({ 
      commercial_status: request.commercial_status || 'pending', 
      commercial_note: request.commercial_note || '' 
    });
    setIsStatusDialogOpen(true);
  };

  const openFollowupDialog = (request) => {
    setSelectedRequest(request);
    setFollowupForm({ followup_type: 'call', content: '', next_followup_date: '', outcome: '' });
    setIsFollowupDialogOpen(true);
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { 
        day: 'numeric', month: 'short', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('fr-FR', { 
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getCollaboratorName = (id) => {
    const collab = collaborators.find(c => c.id === id);
    return collab ? collab.name : '-';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/company/clients')}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-heading font-semibold">{client.name}</h1>
          <p className="text-muted-foreground">
            Client depuis {client.created_at ? formatDate(client.created_at) : '-'}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/company/clients/${clientId}/edit`)}>
          <Edit className="w-4 h-4 mr-2" /> Modifier
        </Button>
      </div>

      {/* Client Info + Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Coordonnées</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold">{client.name}</p>
              </div>
            </div>
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <a href={`mailto:${client.email}`} className="text-sm hover:text-primary">{client.email}</a>
              </div>
              {client.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <a href={`tel:${client.phone}`} className="text-sm hover:text-primary">{client.phone}</a>
                </div>
              )}
              {client.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <span className="text-sm">{client.address}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Activité</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Créneaux réalisés / planifiés */}
              <div className="p-3 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-blue-600 font-medium">Créneaux</span>
                </div>
                <p className="text-xl font-bold text-blue-700">
                  {stats?.completed_bookings || 0}/{stats?.total_bookings || 0}
                </p>
                <p className="text-xs text-blue-600">réalisés / total</p>
              </div>
              
              {/* RDV Devis */}
              <div className="p-3 bg-amber-50 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-amber-600" />
                  <span className="text-xs text-amber-600 font-medium">RDV Devis</span>
                </div>
                <p className="text-xl font-bold text-amber-700">
                  {stats?.quote_requests_transformed || 0}/{stats?.quote_requests_total || 0}
                </p>
                <p className="text-xs text-amber-600">transformés</p>
              </div>
              
              {/* CA Total */}
              <div className="p-3 bg-green-50 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <Euro className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-green-600 font-medium">CA Total</span>
                </div>
                <p className="text-xl font-bold text-green-700">
                  {stats?.total_revenue?.toFixed(0) || 0}€
                </p>
              </div>
              
              {/* Échanges */}
              <div className="p-3 bg-purple-50 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-purple-600 font-medium">Échanges</span>
                </div>
                <p className="text-xl font-bold text-purple-700">{exchanges.length}</p>
              </div>
            </div>

            {/* Next booking */}
            {stats?.next_booking && (
              <div className="mt-4 p-3 border rounded-xl bg-primary/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CalendarClock className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Prochain créneau</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(stats.next_booking.date)} {stats.next_booking.start_time && `à ${stats.next_booking.start_time}`}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline">{stats.next_booking.service_name || 'Intervention'}</Badge>
                </div>
              </div>
            )}

            {/* Subscription Info if exists */}
            {client.subscription && (
              <div className="mt-4 p-3 border rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" /> Abonnement
                  </p>
                  <Badge className={client.subscription.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {client.subscription.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Fréquence</p>
                    <p className="font-medium">
                      {client.subscription.frequency === 'weekly' ? 'Hebdo' :
                       client.subscription.frequency === 'biweekly' ? 'Bi-mensuel' :
                       client.subscription.frequency === 'monthly' ? 'Mensuel' : client.subscription.frequency}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-medium">{client.subscription.slot_type === 'half_day' ? '½ journée' : 'Journée'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Passages/an</p>
                    <p className="font-medium">{client.subscription.passages_per_year || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Prestations</TabsTrigger>
          <TabsTrigger value="quotes">
            RDV Devis ({quoteRequests.length})
          </TabsTrigger>
          <TabsTrigger value="exchanges">Échanges ({exchanges.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Prestations Tab */}
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historique des prestations</CardTitle>
            </CardHeader>
            <CardContent>
              {client.bookings && client.bookings.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Prestation</TableHead>
                      <TableHead>Intervenant</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {client.bookings.sort((a, b) => new Date(b.date) - new Date(a.date)).map((booking, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <p className="font-medium">{formatDate(booking.date)}</p>
                          <p className="text-xs text-muted-foreground">
                            {booking.slot_type === 'half_day' ? '½ journée' : 'Journée'}
                          </p>
                        </TableCell>
                        <TableCell>{booking.service_name || booking.reason || 'Intervention'}</TableCell>
                        <TableCell>{getCollaboratorName(booking.collaborator_id)}</TableCell>
                        <TableCell className="font-medium">
                          {(booking.total_price || booking.invoice_amount || 0).toFixed(0)}€
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            booking.status === 'completed' ? 'default' : 
                            booking.status === 'confirmed' ? 'secondary' : 'outline'
                          }>
                            {booking.status === 'completed' ? 'Terminé' : 
                             booking.status === 'confirmed' ? 'Confirmé' : 
                             booking.status === 'cancelled' ? 'Annulé' : 
                             booking.status === 'pending' ? 'En attente' : booking.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune prestation enregistrée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* RDV Devis Tab */}
        <TabsContent value="quotes">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">RDV & Demandes de Devis</CardTitle>
              <CardDescription>
                Suivez le statut commercial de chaque demande de devis
              </CardDescription>
            </CardHeader>
            <CardContent>
              {quoteRequests.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° / Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Statut commercial</TableHead>
                      <TableHead>Relances</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quoteRequests.map((request) => {
                      const commercialStatus = COMMERCIAL_STATUS[request.commercial_status] || COMMERCIAL_STATUS.pending;
                      const StatusIcon = commercialStatus.icon;
                      
                      return (
                        <TableRow key={request.id}>
                          <TableCell>
                            <p className="font-mono text-sm">{request.request_number || `#${request.id?.substring(0, 8)}`}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(request.created_at)}</p>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {request.booking_type === 'appointment' ? 'RDV' : 'Devis'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={commercialStatus.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {commercialStatus.label}
                            </Badge>
                            {request.commercial_note && (
                              <p className="text-xs text-muted-foreground mt-1 max-w-[200px] truncate">
                                {request.commercial_note}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            {request.followups?.length > 0 ? (
                              <div className="flex items-center gap-1">
                                <Badge variant="secondary">{request.followups.length}</Badge>
                                <span className="text-xs text-muted-foreground">
                                  Dernière: {formatDateTime(request.followups[request.followups.length - 1]?.created_at)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Aucune</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openStatusDialog(request)}
                              >
                                Statut
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openFollowupDialog(request)}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Relance
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune demande de devis</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exchanges Tab */}
        <TabsContent value="exchanges">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Suivi des échanges</CardTitle>
                <Button onClick={() => setIsExchangeDialogOpen(true)} size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un échange
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {exchanges.length > 0 ? (
                <div className="space-y-3">
                  {exchanges.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((exchange) => {
                    const TypeIcon = FOLLOWUP_TYPES[exchange.exchange_type]?.icon || FileText;
                    return (
                      <div key={exchange.id} className="flex gap-4 p-4 border rounded-lg">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          exchange.exchange_type === 'call' ? 'bg-blue-100 text-blue-600' :
                          exchange.exchange_type === 'email' ? 'bg-purple-100 text-purple-600' :
                          exchange.exchange_type === 'sms' ? 'bg-green-100 text-green-600' :
                          exchange.exchange_type === 'meeting' ? 'bg-amber-100 text-amber-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize">
                                {exchange.exchange_type}
                              </Badge>
                              <Badge variant={exchange.direction === 'incoming' ? 'secondary' : 'outline'}>
                                {exchange.direction === 'incoming' ? 'Entrant' : 'Sortant'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {formatDateTime(exchange.created_at)}
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive"
                                onClick={() => handleDeleteExchange(exchange.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          {exchange.subject && <p className="font-medium text-sm">{exchange.subject}</p>}
                          <p className="text-sm text-muted-foreground">{exchange.content}</p>
                          {exchange.duration_minutes && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Durée: {exchange.duration_minutes} min
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun échange enregistré</p>
                  <Button variant="outline" className="mt-4" onClick={() => setIsExchangeDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Ajouter le premier échange
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Notes internes</CardTitle>
            </CardHeader>
            <CardContent>
              {client.notes ? (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{client.notes}</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune note pour ce client</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Status Update Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le statut commercial</DialogTitle>
            <DialogDescription>
              Mettez à jour le statut de ce RDV/devis
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select 
                value={statusForm.commercial_status} 
                onValueChange={(v) => setStatusForm(prev => ({ ...prev, commercial_status: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un statut" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(COMMERCIAL_STATUS).map(([value, config]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (optionnel)</Label>
              <Textarea
                value={statusForm.commercial_note}
                onChange={(e) => setStatusForm(prev => ({ ...prev, commercial_note: e.target.value }))}
                placeholder="Ajouter une note..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleUpdateStatus}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Followup Dialog */}
      <Dialog open={isFollowupDialogOpen} onOpenChange={setIsFollowupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une relance</DialogTitle>
            <DialogDescription>
              Enregistrez une action de relance
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type de relance</Label>
              <Select 
                value={followupForm.followup_type} 
                onValueChange={(v) => setFollowupForm(prev => ({ ...prev, followup_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FOLLOWUP_TYPES).map(([value, config]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Détail de la relance *</Label>
              <Textarea
                value={followupForm.content}
                onChange={(e) => setFollowupForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Décrivez la relance effectuée..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Résultat (optionnel)</Label>
              <Input
                value={followupForm.outcome}
                onChange={(e) => setFollowupForm(prev => ({ ...prev, outcome: e.target.value }))}
                placeholder="Ex: A rappeler la semaine prochaine"
              />
            </div>
            <div className="space-y-2">
              <Label>Prochaine relance prévue (optionnel)</Label>
              <Input
                type="date"
                value={followupForm.next_followup_date}
                onChange={(e) => setFollowupForm(prev => ({ ...prev, next_followup_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFollowupDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddFollowup}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Exchange Dialog */}
      <Dialog open={isExchangeDialogOpen} onOpenChange={setIsExchangeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un échange</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={exchangeForm.exchange_type} 
                  onValueChange={(v) => setExchangeForm(prev => ({ ...prev, exchange_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Appel</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="meeting">RDV</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Direction</Label>
                <Select 
                  value={exchangeForm.direction} 
                  onValueChange={(v) => setExchangeForm(prev => ({ ...prev, direction: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outgoing">Sortant</SelectItem>
                    <SelectItem value="incoming">Entrant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input
                value={exchangeForm.subject}
                onChange={(e) => setExchangeForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Sujet de l'échange"
              />
            </div>
            <div className="space-y-2">
              <Label>Contenu</Label>
              <Textarea
                value={exchangeForm.content}
                onChange={(e) => setExchangeForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Détails de l'échange..."
                rows={3}
              />
            </div>
            {exchangeForm.exchange_type === 'call' && (
              <div className="space-y-2">
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  value={exchangeForm.duration_minutes}
                  onChange={(e) => setExchangeForm(prev => ({ ...prev, duration_minutes: e.target.value }))}
                  placeholder="5"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExchangeDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddExchange}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
